import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FiAlertTriangle,
  FiBookOpen,
  FiCheckCircle,
  FiDatabase,
  FiDownload,
  FiEye,
  FiFileText,
  FiMessageSquare,
  FiPaperclip,
  FiPlus,
  FiPrinter,
  FiRefreshCw,
  FiSave,
  FiSend,
  FiSettings,
  FiTerminal,
  FiTrash2,
  FiUpload,
  FiZap,
} from "react-icons/fi";
import {
  applySpecsToHtml,
  buildDailyReportSampleHtml,
  compileProposalLocally,
  countFilledFields,
  extractHtmlFromResponse,
  hasTakeoffFacts,
  parseTakeoffSpecs,
  wantsConstructionBidLayout,
} from "../utils/compileProposalLocally";
import {
  buildConstructionBidStarter,
  MATCH_LAYOUT_SYSTEM,
} from "../utils/matchLayoutTemplate";
import ReportFieldPicker from "./ReportFieldPicker";
import {
  buildFieldsPrompt,
  describeField,
  ensureFieldsInHtml,
  extractFieldKeys,
} from "../utils/reportFieldCatalog";
import { aiSettingsService } from "../services/api/aiSettingsService";
import { proposalTemplatesService } from "../services/api/proposalTemplatesService";
import {
  getActiveChatId,
  getAnthropicModel,
  getApiKey,
  loadChatThreads,
  loadModelCatalog,
  newChatId,
  saveAiCredentials,
  saveModelCatalog,
  setActiveChatId,
  upsertChatThread,
} from "../utils/reportsStorage";

const DAILY_REPORT_DOC = "Daily report · Elastizell 250 PSI LWIC";
const DOC_TYPES = ["Proposal", "Budget", "Revision", "Change Order", "Project Report", DAILY_REPORT_DOC];
const TEMPLATE_VALUE_PREFIX = "tpl:";

const MODEL_CATALOG_TTL_MS = 12 * 60 * 60 * 1000;
const FIELD_KEYS_STORAGE = "nettles_report_field_keys_v1";

function loadStoredFieldKeys() {
  try {
    const parsed = JSON.parse(localStorage.getItem(FIELD_KEYS_STORAGE) || "[]");
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === "string") : [];
  } catch {
    return [];
  }
}

function uniqueIds(ids) {
  return [...new Set((ids || []).map((id) => String(id || "").trim()).filter(Boolean))];
}

async function fetchAnthropicModelIds(apiKey) {
  const ids = [];
  let after = "";
  for (let page = 0; page < 8; page += 1) {
    const url = new URL("https://api.anthropic.com/v1/models");
    url.searchParams.set("limit", "100");
    if (after) url.searchParams.set("after_id", after);
    const res = await fetch(url.toString(), {
      headers: {
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error?.message || `Anthropic models ${res.status}`);
    ids.push(...(json.data || []).map((row) => row.id));
    if (!json.has_more || !json.last_id) break;
    after = json.last_id;
  }
  return uniqueIds(ids);
}

const ANTHROPIC_MODELS = [
  "claude-sonnet-5",
  "claude-opus-5",
  "claude-haiku-4-5",
  "claude-sonnet-4-6",
];

const SYSTEM_BASE = `You are Nettles Agent — the proposal and reporting specialist for Nettles Construction Solutions.

You are in a live chat. The estimator's API key is connected, so you answer and you do the work. Do not hand the task back to a local filler.

Use the nettles-template-builder skill and house-rules.md (house rules override skill defaults).

Each turn, pick one:

1. Talk — the user asked a question, corrected you, or is still deciding. Reply in short markdown. Do not output HTML on that turn.
2. Act — the user asked you to make, build, change, clone, or fill a proposal, bid, report, or template. Do that work. Start with a short markdown note of what you changed, then one full self-contained HTML document in a \`\`\`html fence (keep toolbar, class="f" fields, styles, scripts). Then \`\`\`json {"reviewerNotes":["..."]}.

Rules for both:
- Never invent prices, quantities, dates, or spec numbers. Leave those as empty highlighted placeholders.
- Never copy the user's question or chat sentence into Project Name or any other field.
- Fill a field only when they gave that fact (project name:, a labeled takeoff, a price, a quantity).
- If they want a construction-standards / construction-bid layout, build that blue bid (owner | contractor, Scope, Materials, Labor, Costs, Time Table), not the Nettles BUDGET PROPOSAL, unless they asked for the house template.
`;

function agentReplyText(raw) {
  const text = String(raw || "")
    .replace(/```json[\s\S]*?```/gi, "")
    .replace(/```html[\s\S]*?(```|$)/gi, "")
    .trim();
  return text;
}

function ChatBody({ text }) {
  const lines = String(text || "").split("\n");
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g).filter((p) => p !== "");
        const inline = parts.map((part, j) =>
          part.startsWith("**") && part.endsWith("**") ? (
            <strong key={j}>{part.slice(2, -2)}</strong>
          ) : (
            <span key={j}>{part}</span>
          )
        );
        if (!line.trim()) return <div key={i} className="h-2" />;
        return (
          <div key={i} className="whitespace-pre-wrap">
            {inline}
          </div>
        );
      })}
    </div>
  );
}

const WELCOME = {
  id: "welcome",
  role: "assistant",
  text:
    "I'm **Nettles Agent**.\n\n1. Use the settings icon beside **Preview** to choose the Anthropic model and key\n2. Optional: **Match file** to clone a layout from an image/HTML\n3. Paste takeoff / ask for a proposal\n4. **Save chat** or **Save as template** when you're happy with the result",
  time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
};

function nowTime() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatChangedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function applySharedSettings(rec, setApiKey, setAnthropicModel, setSettingsMeta) {
  const savedKey = typeof rec?.apiKey === "string" ? rec.apiKey : "";
  const savedModel = rec?.model || ANTHROPIC_MODELS[0];
  setApiKey(savedKey);
  setAnthropicModel(savedModel);
  setSettingsMeta({
    updatedBy: rec?.updatedBy || "",
    updatedAt: rec?.updatedAt || "",
  });
  saveAiCredentials({ apiKey: savedKey, provider: "anthropic", anthropicModel: savedModel });
}

function titleFromMessages(messages) {
  const firstUser = messages.find((m) => m.role === "user");
  if (!firstUser?.text) return "New chat";
  return firstUser.text.replace(/\s+/g, " ").trim().slice(0, 48) || "New chat";
}

function ModelPicker({ label, value, onChange, models, busy }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const q = query.trim().toLowerCase();
  const shown = models.filter((id) => !q || id.toLowerCase().includes(q));

  return (
    <div className="relative" ref={rootRef}>
      <label className="text-[10px] font-bold text-slate-400 uppercase">{label}</label>
      <button
        type="button"
        onClick={() => {
          setOpen((isOpen) => !isOpen);
          setQuery("");
        }}
        className="mt-1 flex w-[240px] items-center justify-between gap-2 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs font-mono text-left"
      >
        <span className="truncate">{value || "Select a model"}</span>
        <span className="text-slate-400 text-[10px]">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-[280px] rounded-xl border border-slate-200 bg-white text-slate-800 shadow-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={busy ? "Loading models..." : "Search model..."}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-slate-400"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {shown.map((id) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(id);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`block w-full px-3 py-1.5 text-left text-xs font-mono hover:bg-slate-100 ${
                    id === value ? "bg-slate-100 font-semibold" : ""
                  }`}
                >
                  {id}
                </button>
              </li>
            ))}
            {!shown.length && (
              <li className="px-3 py-2 text-xs text-slate-400">No models</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function ReportsProposalTemplates() {
  const [activeTab, setActiveTab] = useState("chat");
  const [showSettings, setShowSettings] = useState(false);
  const [toast, setToast] = useState("");

  const [sessionId, setSessionId] = useState(null);
  const [streamIndex, setStreamIndex] = useState(0);

  const [apiKey, setApiKey] = useState(() => getApiKey());
  const [anthropicModel, setAnthropicModel] = useState(() => getAnthropicModel() || ANTHROPIC_MODELS[0]);
  const [settingsMeta, setSettingsMeta] = useState({ updatedBy: "", updatedAt: "" });
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [anthropicModels, setAnthropicModels] = useState(() => {
    const cached = loadModelCatalog().anthropic;
    return cached.length ? cached : ANTHROPIC_MODELS;
  });
  const [modelsBusy, setModelsBusy] = useState(false);

  const [docType, setDocType] = useState("Proposal");
  const [officeLocation, setOfficeLocation] = useState("corporate");
  const [clientLogoBase64, setClientLogoBase64] = useState("");

  const [skillMd, setSkillMd] = useState("");
  const [rulesMd, setRulesMd] = useState("");
  const [parsedRules, setParsedRules] = useState([]);

  const [referenceFile, setReferenceFile] = useState(null); // { name, text?, dataUrl?, mediaType?, isImage }

  const [chatId, setChatId] = useState(() => getActiveChatId() || newChatId());
  const [chatThreads, setChatThreads] = useState(() => loadChatThreads());
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const bottomRef = useRef(null);
  const skipPersist = useRef(false);
  const skipNextPreviewLoad = useRef(false);

  const [htmlOut, setHtmlOut] = useState("");
  const [notes, setNotes] = useState([]);
  const [canvasTab, setCanvasTab] = useState("preview");
  const [showCanvas, setShowCanvas] = useState(true);
  const [saveTplName, setSaveTplName] = useState("");
  const [companyTemplates, setCompanyTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatesBusy, setTemplatesBusy] = useState(false);
  const [fieldPickerOpen, setFieldPickerOpen] = useState(false);
  const [selectedFieldKeys, setSelectedFieldKeys] = useState(loadStoredFieldKeys);
  const [fieldSamples, setFieldSamples] = useState({});

  useEffect(() => {
    try {
      localStorage.setItem(FIELD_KEYS_STORAGE, JSON.stringify(selectedFieldKeys));
    } catch {
      /* storage unavailable */
    }
  }, [selectedFieldKeys]);

  const selectedFieldDefs = React.useMemo(
    () => selectedFieldKeys.map((key) => describeField(key)),
    [selectedFieldKeys]
  );

  const toastMsg = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  }, []);

  const templateErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

  const refreshCompanyTemplates = useCallback(async () => {
    const list = await proposalTemplatesService.list();
    setCompanyTemplates(Array.isArray(list) ? list : []);
    return Array.isArray(list) ? list : [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    refreshCompanyTemplates().catch((error) => {
      if (!cancelled) {
        toastMsg(templateErrorMessage(error, "Could not load company templates"));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [refreshCompanyTemplates, toastMsg]);

  const selectCompanyTemplate = async (id) => {
    if (!id) {
      setSelectedTemplateId("");
      setSelectedTemplate(null);
      return;
    }

    setTemplatesBusy(true);
    try {
      const rec = await proposalTemplatesService.get(id);
      setSelectedTemplateId(String(rec.id));
      setSelectedTemplate(rec);
      if (rec.docType) setDocType(rec.docType);
      // Show which API fields this template already uses.
      const used = extractFieldKeys(rec.html);
      if (used.length) setSelectedFieldKeys(used);
    } catch (error) {
      toastMsg(templateErrorMessage(error, "Could not load template"));
    } finally {
      setTemplatesBusy(false);
    }
  };

  const onTemplatePickerChange = (value) => {
    if (value.startsWith(TEMPLATE_VALUE_PREFIX)) {
      selectCompanyTemplate(value.slice(TEMPLATE_VALUE_PREFIX.length));
      return;
    }
    setSelectedTemplateId("");
    setSelectedTemplate(null);
    setDocType(value);
  };

  const saveApiKeyAndClose = async () => {
    const key = apiKey.trim();
    if (!key.startsWith("sk-ant-")) {
      toastMsg("The API key must start with sk-ant-");
      return;
    }
    setSettingsBusy(true);
    try {
      const rec = await aiSettingsService.save({ apiKey: key, model: anthropicModel });
      applySharedSettings(rec, setApiKey, setAnthropicModel, setSettingsMeta);
      setShowSettings(false);
      toastMsg(rec?.updatedBy ? `Saved for everyone by ${rec.updatedBy}` : "Model and key saved for everyone");
    } catch (error) {
      toastMsg(templateErrorMessage(error, "Could not save AI settings"));
    } finally {
      setSettingsBusy(false);
    }
  };

  const settingsRef = useRef({ apiKey });
  settingsRef.current = { apiKey };

  const refreshModelCatalog = useCallback(async ({ force = false } = {}) => {
    const cached = loadModelCatalog();
    const fresh = !force && cached.at && Date.now() - cached.at < MODEL_CATALOG_TTL_MS;
    const key = settingsRef.current.apiKey.trim();
    if (fresh && cached.anthropic.length) {
      setAnthropicModels(cached.anthropic);
      return;
    }
    if (!key.startsWith("sk-ant-")) return;

    setModelsBusy(true);
    try {
      const anthropic = await fetchAnthropicModelIds(key);
      setAnthropicModels(anthropic);
      saveModelCatalog({ anthropic });
    } catch (err) {
      if (force) toastMsg(err.message || "Anthropic model list failed");
    } finally {
      setModelsBusy(false);
    }
  }, [toastMsg]);

  useEffect(() => {
    let cancelled = false;
    aiSettingsService
      .get()
      .then((rec) => {
        if (cancelled) return;
        applySharedSettings(rec, setApiKey, setAnthropicModel, setSettingsMeta);
      })
      .catch((error) => {
        if (!cancelled) toastMsg(templateErrorMessage(error, "Could not load AI settings"));
      });
    return () => {
      cancelled = true;
    };
  }, [toastMsg]);

  useEffect(() => {
    if (!showSettings) return;
    refreshModelCatalog();
  }, [showSettings, refreshModelCatalog]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  // Restore active chat once
  useEffect(() => {
    const id = getActiveChatId();
    const threads = loadChatThreads();
    const found = id ? threads.find((t) => t.id === id) : null;
    if (found?.messages?.length) {
      skipPersist.current = true;
      setChatId(found.id);
      setMessages(found.messages);
      setSessionId(found.sessionId || null);
      setStreamIndex(found.streamIndex || 0);
      if (found.htmlOut) {
        skipNextPreviewLoad.current = true;
        setHtmlOut(found.htmlOut);
      }
      if (found.notes) setNotes(found.notes);
    }
  }, []);

  // Persist chat
  useEffect(() => {
    if (skipPersist.current) {
      skipPersist.current = false;
      return;
    }
    if (!messages.length || (messages.length === 1 && messages[0].id === "welcome")) return;
    const thread = upsertChatThread({
      id: chatId,
      title: titleFromMessages(messages),
      messages,
      sessionId,
      streamIndex,
      htmlOut,
      notes,
    });
    setChatThreads(loadChatThreads());
    setActiveChatId(thread.id);
  }, [messages, sessionId, streamIndex, htmlOut, notes, chatId]);

  const parseRules = useCallback((md) => {
    const cats = [
      "Style & voice",
      "Scope & clause language",
      "Pricing & commercial terms",
      "Client & owner specifics",
      "Workflow & output",
      "Retired",
    ];
    const rules = [];
    let cat = "";
    md.split("\n").forEach((line) => {
      const t = line.trim();
      if (t.startsWith("##")) {
        const c = t.replace("##", "").trim();
        if (cats.includes(c)) cat = c;
      } else if (t.startsWith("-") && cat) {
        rules.push({ category: cat, text: t.replace("-", "").trim() });
      }
    });
    setParsedRules(rules);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [skillRes, rulesRes] = await Promise.all([
          fetch("/SKILL.md"),
          fetch("/house-rules.md"),
        ]);
        if (skillRes.ok) setSkillMd(await skillRes.text());
        if (rulesRes.ok) {
          const text = await rulesRes.text();
          setRulesMd(text);
          parseRules(text);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [parseRules]);

  const finalizeHtml = useCallback(async (raw) => {
    let html = raw;
    try {
      const logoRes = await fetch("/assets/nettles_logo_color.png");
      if (logoRes.ok) {
        const blob = await logoRes.blob();
        const dataUrl = await new Promise((resolve) => {
          const r = new FileReader();
          r.onloadend = () => resolve(r.result);
          r.readAsDataURL(blob);
        });
        html = html.replace("{{LOGO_SRC}}", dataUrl);
      }
    } catch {
      /* ignore */
    }
    if (clientLogoBase64) {
      html = html.replace(
        "{{CLIENT_LOGO}}",
        `<img src="${clientLogoBase64}" alt="Client" style="max-height:0.55in;width:auto;margin:0 0 6px auto;">`,
      );
    } else {
      html = html.replace("{{CLIENT_LOGO}}", "");
    }
    const offices = {
      corporate: "Corporate: 2850 Fairfax Trfy, Kansas City, KS 66115 | 816.561.7667",
      houston: "Houston: 41229 Park 290 Dr., Waller, TX 77484 | 936.372.9020",
    };
    html = html.replace("{{OFFICE_LINE}}", offices[officeLocation] || offices.corporate);
    return html;
  }, [clientLogoBase64, officeLocation]);

  const loadSelectedTemplatePreview = useCallback(async () => {
    try {
      let html = "";
      if (selectedTemplate?.html) {
        html = selectedTemplate.html;
      } else if (docType === DAILY_REPORT_DOC) {
        html = buildDailyReportSampleHtml();
      } else {
        const templateRes = await fetch("/proposal_template.html");
        html = templateRes.ok ? await templateRes.text() : "";
        if (html) html = applySpecsToHtml(html, {}, docType);
      }
      if (!html) return "";
      return finalizeHtml(html);
    } catch (error) {
      console.error(error);
      return "";
    }
  }, [selectedTemplate, docType, finalizeHtml]);

  useEffect(() => {
    if (skipNextPreviewLoad.current) {
      skipNextPreviewLoad.current = false;
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const html = await loadSelectedTemplatePreview();
      if (!cancelled && html) {
        setHtmlOut(html);
        setShowCanvas(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadSelectedTemplatePreview]);

  const resolveBaseTemplateHtml = async () => {
    // Match-file mode: do NOT start from Nettles BUDGET PROPOSAL house template
    if (referenceFile) {
      const specs = parseTakeoffSpecs("");
      const offices = {
        corporate: "Corporate: 2850 Fairfax Trfy, Kansas City, KS 66115 | 816.561.7667",
        houston: "Houston: 41229 Park 290 Dr., Waller, TX 77484 | 936.372.9020",
      };
      const starter = buildConstructionBidStarter(specs, offices[officeLocation] || offices.corporate);
      return {
        html: referenceFile.isImage
          ? starter
          : referenceFile.text && /<html|<div|<h1/i.test(referenceFile.text)
            ? referenceFile.text
            : starter,
        label: `Match layout · ${referenceFile.name}`,
        matchMode: true,
      };
    }
    if (selectedTemplate?.html) {
      return {
        html: selectedTemplate.html,
        label: `Company template · ${selectedTemplate.name}`,
        matchMode: false,
      };
    }
    const templateRes = await fetch("/proposal_template.html");
    const html = templateRes.ok ? await templateRes.text() : "";
    return { html, label: "Nettles default proposal_template", matchMode: false };
  };

  const callAnthropic = async (system, userContent, history = []) => {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "x-api-key": apiKey.trim(),
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        model: anthropicModel,
        max_tokens: 16000,
        system,
        messages: [...history, { role: "user", content: userContent }],
      }),
    });
    const json = await res.json();
    if (!res.ok || json.error) throw new Error(json.error?.message || `Anthropic ${res.status}`);
    const blocks = Array.isArray(json.content) ? json.content : [];
    const text = blocks
      .filter((block) => block?.type === "text" && typeof block.text === "string")
      .map((block) => block.text)
      .join("\n")
      .trim();
    if (!text) throw new Error("Anthropic returned no text");
    return text;
  };

  const buildDirectPrompts = (userText, base) => {
    if (base.matchMode) {
      const system = `${MATCH_LAYOUT_SYSTEM}

## house-rules.md (apply standing preferences when relevant)
${(rulesMd || "").slice(0, 6000)}
`;
      const textPart = `Clone the attached reference as a new editable HTML template.

UI doc type tip: ${docType}
Office: ${officeLocation === "houston" ? "Houston" : "Corporate"}

Do NOT produce the Nettles BUDGET PROPOSAL / red house template.
Match the reference: blue title + section underlines, two-column parties, Scope, Materials (2-col), Labor, Costs table, Time Table, closing/contact.

Structural starter you may refine:
\`\`\`html
${base.html.slice(0, 60000)}
\`\`\`

User request / takeoff / fill instructions:
${userText}

Return \`\`\`html\`\`\` then \`\`\`json {"reviewerNotes":[...]}\`\`\` when you are building or updating the document. If the user is only asking a question, answer in markdown and do not return HTML. Never put their chat sentence into Project Name.`;

      if (referenceFile?.isImage && referenceFile.dataUrl) {
        const b64 = referenceFile.dataUrl.split(",")[1] || "";
        const anthropicContent = [
          {
            type: "image",
            source: {
              type: "base64",
              media_type: referenceFile.mediaType || "image/png",
              data: b64,
            },
          },
          { type: "text", text: textPart },
        ];
        return { system, anthropicContent, matchMode: true };
      }

      const withTextRef = referenceFile?.text
        ? `${textPart}\n\nReference file text:\n\`\`\`\n${referenceFile.text.slice(0, 80000)}\n\`\`\``
        : textPart;
      return {
        system,
        anthropicContent: withTextRef,
        matchMode: true,
      };
    }

    const system = `${SYSTEM_BASE}

## nettles-template-builder skill
${(skillMd || "(skill not loaded)").slice(0, 14000)}

## house-rules.md
${rulesMd || "(no house rules yet)"}
`;
    const user = `Document type: ${docType}
Office: ${officeLocation === "houston" ? "Houston" : "Corporate"}
Selected base template: ${base.label}

Base template HTML:
${base.html.slice(0, 120000)}

User request / takeoff:
${userText}

Prior chat is in the message history. You are the agent on their API key — answer and do the work.
If they are asking or correcting, reply in markdown only.
If they want a document made or changed, reply with a short note, then \`\`\`html, then \`\`\`json {"reviewerNotes":[...]}\`\`\`.
Never copy their chat sentence into Project Name.`;
    return {
      system,
      anthropicContent: user,
      matchMode: false,
    };
  };

  const send = async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || busy) return;

    setMessages((m) => [
      ...m,
      {
        id: `u_${Date.now()}`,
        role: "user",
        text: referenceFile
          ? `${text}\n\n_(matching reference: ${referenceFile.name})_`
          : text,
        time: nowTime(),
      },
    ]);
    if (!overrideText) setInput("");
    setBusy(true);
    let baseHtmlForFields = "";

    try {
      setStatus(referenceFile ? "Agent · matching reference layout..." : "Agent · reading your message...");
      let base = await resolveBaseTemplateHtml();
      baseHtmlForFields = base.html || "";
      if (!referenceFile && !selectedTemplate && wantsConstructionBidLayout(text)) {
        const offices = {
          corporate: "Corporate: 2850 Fairfax Trfy, Kansas City, KS 66115 | 816.561.7667",
          houston: "Houston: 41229 Park 290 Dr., Waller, TX 77484 | 936.372.9020",
        };
        base = {
          html: buildConstructionBidStarter({}, offices[officeLocation] || offices.corporate),
          label: "Construction Bid Proposal",
          matchMode: true,
        };
      }
      baseHtmlForFields = base.html || "";
      const specs = parseTakeoffSpecs(text);
      let responseText = "";
      let viaDirect = false;
      let local = false;
      const history = messages
        .filter((m) => m.id !== "welcome" && m.text)
        .slice(-8)
        .map((m) => ({
          role: m.role === "assistant" ? "assistant" : "user",
          content: String(m.text).slice(0, 2000),
        }));

      const key = apiKey.trim();

      const callDirectProvider = async () => {
        viaDirect = true;
        setStatus(`Anthropic · ${anthropicModel}`);
        const fieldsBlock = buildFieldsPrompt(selectedFieldDefs, fieldSamples);
        const prompts = buildDirectPrompts(fieldsBlock ? `${text}\n\n${fieldsBlock}` : text, base);
        responseText = await callAnthropic(prompts.system, prompts.anthropicContent, history);
      };

      if (key) {
        await callDirectProvider();
      } else if (base.matchMode) {
        setStatus("No API key — using construction-bid layout starter...");
        local = true;
        const offices = {
          corporate: "Corporate: 2850 Fairfax Trfy, Kansas City, KS 66115 | 816.561.7667",
          houston: "Houston: 41229 Park 290 Dr., Waller, TX 77484 | 936.372.9020",
        };
        const starter = buildConstructionBidStarter(specs, offices[officeLocation] || offices.corporate);
        responseText = `\`\`\`html\n${starter}\n\`\`\`\n\n\`\`\`json\n{"reviewerNotes":["Built Construction Bid Proposal layout from Match file (add API key for AI clone of the image)."]}\n\`\`\``;
      } else {
        setStatus("No API key — local fill only...");
        local = true;
        const compiled = compileProposalLocally(base.html, text, docType);
        responseText = `\`\`\`html\n${compiled.html}\n\`\`\`\n\n\`\`\`json\n{"reviewerNotes":${JSON.stringify(compiled.reviewerNotes)}}\n\`\`\``;
      }

      const modelHtml = extractHtmlFromResponse(responseText);
      const spoke = agentReplyText(responseText);

      if (!modelHtml && !local) {
        setMessages((m) => [
          ...m,
          {
            id: `a_${Date.now()}`,
            role: "assistant",
            time: nowTime(),
            text: spoke || "I didn’t get a reply. Try sending that again.",
          },
        ]);
        return;
      }

      let rawHtml = modelHtml || "";
      let forcedMatchStarter = false;

      // If AI ignored match mode and returned Nettles house BUDGET, replace with match starter
      if (
        rawHtml &&
        base.matchMode &&
        referenceFile &&
        /BUDGET PROPOSAL|PROJECT PROPOSAL|class="t1"|class="bar"/i.test(rawHtml) &&
        !/Construction Bid Proposal/i.test(rawHtml)
      ) {
        const offices = {
          corporate: "Corporate: 2850 Fairfax Trfy, Kansas City, KS 66115 | 816.561.7667",
          houston: "Houston: 41229 Park 290 Dr., Waller, TX 77484 | 936.372.9020",
        };
        rawHtml = buildConstructionBidStarter(specs, offices[officeLocation] || offices.corporate);
        forcedMatchStarter = true;
      }

      let reviewNotes = [];
      const jsonMatch = responseText.match(/```json([\s\S]*?)```/i);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[1].trim());
          reviewNotes = parsed.reviewerNotes || [];
        } catch {
          /* ignore */
        }
      }
      if (forcedMatchStarter) {
        reviewNotes = [
          ...(reviewNotes || []),
          "Model returned house BUDGET template — swapped to Construction Bid Proposal match layout.",
        ];
      }

      if (rawHtml && hasTakeoffFacts(text)) {
        rawHtml = applySpecsToHtml(rawHtml, specs, docType);
      }

      // Selected API fields must always be in the template, even if the model skipped some.
      if (rawHtml && selectedFieldDefs.length) {
        const ensured = ensureFieldsInHtml(rawHtml, selectedFieldDefs);
        rawHtml = ensured.html;
        reviewNotes = [
          ...(reviewNotes || []),
          ensured.added
            ? `API fields · ${selectedFieldDefs.length - ensured.added}/${selectedFieldDefs.length} placed by the agent, ${ensured.added} added in a "Project data" section`
            : `API fields · all ${selectedFieldDefs.length} placed`,
        ];
      }

      if (rawHtml) {
        const counts = countFilledFields(rawHtml);
        reviewNotes = [
          ...(reviewNotes || []),
          viaDirect
            ? `API key · ${counts.filled}/${counts.total} fields`
            : `Local fill · ${counts.filled}/${counts.total} fields`,
        ];
        const finalHtml = await finalizeHtml(rawHtml);
        setHtmlOut(finalHtml);
        setNotes(reviewNotes.length ? reviewNotes : ["Template updated."]);
        setShowCanvas(true);
        setCanvasTab("preview");
      }

      setMessages((m) => [
        ...m,
        {
          id: `a_${Date.now()}`,
          role: "assistant",
          time: nowTime(),
          text:
            spoke ||
            (rawHtml
              ? `Updated the **${docType}**. Click a highlighted field in the preview to edit it.`
              : `**No API key** — local fill only.`),
          card: Boolean(rawHtml),
        },
      ]);
      if (rawHtml) toastMsg("Template updated");
    } catch (err) {
      console.error(err);
      const authFailed = /invalid x-api-key|authentication|api key/i.test(err?.message || "");
      if (selectedFieldDefs.length && baseHtmlForFields) {
        try {
          const ensured = ensureFieldsInHtml(baseHtmlForFields, selectedFieldDefs);
          const finalHtml = await finalizeHtml(ensured.html);
          setHtmlOut(finalHtml);
          setNotes([
            authFailed
              ? "Anthropic rejected the API key, so the agent did not redesign the layout."
              : `The agent call failed (${err.message}).`,
            `The ${ensured.added || selectedFieldDefs.length} selected API field${selectedFieldDefs.length === 1 ? "" : "s"} were added to the template locally.`,
          ]);
          setShowCanvas(true);
          setCanvasTab("preview");
          setMessages((m) => [
            ...m,
            {
              id: `e_${Date.now()}`,
              role: "assistant",
              time: nowTime(),
              text: authFailed
                ? `⚠️ **The API key was rejected** (\`${err.message}\`), so the agent could not lay the template out.\n\nI still added the **${selectedFieldDefs.length}** selected API field${selectedFieldDefs.length === 1 ? "" : "s"} to the current template. Open the settings icon beside Preview and save a valid key that starts with \`sk-ant-\` if you want the agent to place them itself.\n\nSave as template when the preview looks right.`
                : `⚠️ **Error:** ${err.message}\n\nThe selected API fields were still added to the current template.`,
              card: true,
            },
          ]);
          toastMsg("API fields added locally");
          return;
        } catch (placeErr) {
          console.error(placeErr);
        }
      }
      setMessages((m) => [
        ...m,
        {
          id: `e_${Date.now()}`,
          role: "assistant",
          time: nowTime(),
          text: referenceFile?.isImage
            ? `⚠️ **Error:** ${err.message}\n\nFor image Match file, pick a vision-capable Anthropic model and a valid Anthropic key.`
            : `⚠️ **Error:** ${err.message}\n\nCheck the API key in settings beside Preview. It must start with sk-ant-.`,
        },
      ]);
    } finally {
      setBusy(false);
      setStatus("");
    }
  };

  const loadSample = () => {
    setInput(`Please build a ${docType} using the selected Nettles template.

OU Life Sciences Laboratories BUDGET
Estimated 40,800 SF tapered lightweight insulating concrete (LWIC)
Thicknesses: 6.5 inches to 16.5 inches
Decks: Concrete deck and 1.5 inch steel deck, with temp roofing by others
Spec Section: 035216
Required Base Pricing: $372,000.00
Add options:
P&P Bond: $7,440.00
Mobilizations: 2 included
Additional Mobilization Cost: $7,500.00 each
Sales Tax: not included in base, add if applicable: $16,733.01`);
    toastMsg("Sample takeoff loaded — press Send");
  };

  const fileBaseName = () =>
    `nettles-${docType.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}`;

  const downloadHtml = () => {
    if (!htmlOut) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([htmlOut], { type: "text/html" }));
    a.download = `${fileBaseName()}.html`;
    a.click();
    toastMsg("HTML downloaded");
  };

  const saveAsPdf = () => {
    if (!htmlOut) return;
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Save as PDF");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    document.body.appendChild(iframe);

    const frameDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!frameDoc || !iframe.contentWindow) {
      iframe.remove();
      toastMsg("Could not open PDF print view");
      return;
    }

    frameDoc.open();
    frameDoc.write(htmlOut);
    frameDoc.close();

    const runPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        toastMsg("Choose “Save as PDF” in the print dialog");
      } catch (err) {
        console.error(err);
        toastMsg("PDF print failed — try Open, then print");
      } finally {
        setTimeout(() => iframe.remove(), 1500);
      }
    };

    // Allow layout/images a brief moment before print
    setTimeout(runPrint, 400);
  };

  const openHtml = () => {
    if (!htmlOut) return;
    const w = window.open();
    w.document.write(htmlOut);
    w.document.close();
  };

  const onUploadReference = (file) => {
    if (!file) return;
    const isImage = /^image\//i.test(file.type) || /\.(png|jpe?g|gif|webp|bmp)$/i.test(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      if (isImage) {
        setReferenceFile({
          name: file.name,
          isImage: true,
          dataUrl: String(reader.result || ""),
          mediaType: file.type || "image/png",
        });
      } else {
        setReferenceFile({
          name: file.name,
          isImage: false,
          text: String(reader.result || ""),
        });
      }
      toastMsg(`Match reference attached: ${file.name}`);
    };
    if (isImage) reader.readAsDataURL(file);
    else reader.readAsText(file);
  };

  const onUploadAsSavedTemplate = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      const html = String(reader.result || "");
      const name = window.prompt("Template name:", file.name.replace(/\.[^.]+$/, "")) || file.name;
      if (!name.trim()) return;
      setTemplatesBusy(true);
      try {
        const rec = await proposalTemplatesService.create({
          name: name.trim(),
          html,
          source: "upload",
          docType,
        });
        await refreshCompanyTemplates();
        setSelectedTemplateId(String(rec.id));
        setSelectedTemplate(rec);
        setReferenceFile(null);
        toastMsg(`Template "${rec.name}" published to company library`);
      } catch (error) {
        toastMsg(templateErrorMessage(error, "Could not save template"));
      } finally {
        setTemplatesBusy(false);
      }
    };
    reader.readAsText(file);
  };

  const saveCurrentAsTemplate = async () => {
    if (!htmlOut) {
      toastMsg("Generate a preview first");
      return;
    }
    const name =
      saveTplName.trim() ||
      window.prompt("Name for this template:", `${docType} ${new Date().toLocaleDateString()}`) ||
      "";
    if (!name.trim()) return;
    setTemplatesBusy(true);
    try {
      const rec = await proposalTemplatesService.create({
        name: name.trim(),
        html: htmlOut,
        source: "generated",
        docType,
      });
      setSaveTplName("");
      await refreshCompanyTemplates();
      setSelectedTemplateId(String(rec.id));
      setSelectedTemplate(rec);
      toastMsg(`Template "${rec.name}" published to company library`);
    } catch (error) {
      toastMsg(templateErrorMessage(error, "Could not save template"));
    } finally {
      setTemplatesBusy(false);
    }
  };

  const updateCurrentTemplate = async () => {
    if (!selectedTemplateId) {
      toastMsg("Select a company template to update");
      return;
    }
    if (!htmlOut) {
      toastMsg("Generate a preview first");
      return;
    }
    const name = saveTplName.trim() || selectedTemplate?.name;
    if (!name) {
      toastMsg("Template name is required");
      return;
    }
    setTemplatesBusy(true);
    try {
      const rec = await proposalTemplatesService.update(selectedTemplateId, {
        name,
        html: htmlOut,
        source: "generated",
        docType,
      });
      setSaveTplName("");
      await refreshCompanyTemplates();
      setSelectedTemplate(rec);
      toastMsg(`Template "${rec.name}" updated`);
    } catch (error) {
      toastMsg(templateErrorMessage(error, "Could not update template"));
    } finally {
      setTemplatesBusy(false);
    }
  };

  const deleteSelectedTemplate = async () => {
    if (!selectedTemplateId) return;
    const label = selectedTemplate?.name || "this template";
    if (!window.confirm(`Delete company template "${label}"? This removes it for everyone.`)) {
      return;
    }
    setTemplatesBusy(true);
    try {
      await proposalTemplatesService.remove(selectedTemplateId);
      await refreshCompanyTemplates();
      setSelectedTemplateId("");
      setSelectedTemplate(null);
      toastMsg(`Template "${label}" deleted`);
    } catch (error) {
      toastMsg(templateErrorMessage(error, "Could not delete template"));
    } finally {
      setTemplatesBusy(false);
    }
  };

  const startNewChat = () => {
    const id = newChatId();
    skipPersist.current = true;
    setChatId(id);
    setActiveChatId(id);
    setSessionId(null);
    setStreamIndex(0);
    setMessages([{ ...WELCOME, id: `welcome_${Date.now()}`, time: nowTime() }]);
    setNotes([]);
    setReferenceFile(null);
    loadSelectedTemplatePreview().then((html) => {
      if (html) {
        setHtmlOut(html);
        setShowCanvas(true);
      }
    });
    toastMsg("New chat started");
  };

  const loadChat = (id) => {
    const thread = loadChatThreads().find((t) => t.id === id);
    if (!thread) return;
    skipPersist.current = true;
    setChatId(thread.id);
    setActiveChatId(thread.id);
    setMessages(thread.messages?.length ? thread.messages : [WELCOME]);
    setSessionId(thread.sessionId || null);
    setStreamIndex(thread.streamIndex || 0);
    if (thread.htmlOut) {
      skipNextPreviewLoad.current = true;
      setHtmlOut(thread.htmlOut);
    } else {
      loadSelectedTemplatePreview().then((html) => {
        if (html) {
          setHtmlOut(html);
          setShowCanvas(true);
        }
      });
    }
    setNotes(thread.notes || []);
    toastMsg(`Loaded: ${thread.title}`);
  };

  const saveChatNow = () => {
    upsertChatThread({
      id: chatId,
      title: titleFromMessages(messages),
      messages,
      sessionId,
      streamIndex,
      htmlOut,
      notes,
    });
    setChatThreads(loadChatThreads());
    toastMsg("Chat history saved");
  };

  return (
    <div className="flex flex-col w-full min-h-[720px] bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
      <ReportFieldPicker
        open={fieldPickerOpen}
        onClose={() => setFieldPickerOpen(false)}
        selected={selectedFieldKeys}
        onChange={setSelectedFieldKeys}
        onSamples={setFieldSamples}
      />
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-slate-900 text-white rounded-xl text-sm shadow-xl">
          <FiCheckCircle className="text-emerald-400" /> {toast}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="font-extrabold text-base">Nettles Agent</h1>
          <p className="text-[11px] text-slate-500">
            {settingsMeta.updatedBy
              ? `Last changed by ${settingsMeta.updatedBy}`
              : "Shared Anthropic model and key"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            {[
              { id: "chat", label: "Chat", icon: <FiMessageSquare /> },
              { id: "skill", label: "Skill", icon: <FiZap /> },
              { id: "rules", label: `Rules (${parsedRules.length})`, icon: <FiBookOpen /> },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg ${
                  activeTab === t.id ? "bg-white dark:bg-slate-900 text-red-600 shadow-sm" : "text-slate-500"
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
          {htmlOut && (
            <button
              type="button"
              onClick={() => setShowCanvas((s) => !s)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-800"
            >
              <FiEye /> Preview
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowSettings((s) => !s)}
            className={`flex items-center justify-center w-9 h-9 rounded-xl border ${
              showSettings
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white dark:bg-slate-800 text-slate-600 border-slate-200 dark:border-slate-700"
            }`}
            title={settingsMeta.updatedBy ? `Last changed by ${settingsMeta.updatedBy}` : "Model and API key"}
            aria-label="Model and API key settings"
          >
            <FiSettings />
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="bg-slate-900 text-slate-100 px-5 py-4 border-b border-slate-800 space-y-3">
          <p className="text-xs text-slate-400">
            This model and key are shared with everyone. Saving stores them for the whole team. Your name is recorded from your sign-in.
          </p>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <ModelPicker
              label="Anthropic model"
              value={anthropicModel}
              onChange={setAnthropicModel}
              models={anthropicModels}
              busy={modelsBusy}
            />
            <div className="flex-1 w-full">
              <label className="text-[10px] font-bold text-slate-400 uppercase">API Key</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveApiKeyAndClose();
                  }
                }}
                placeholder="sk-ant-..."
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs font-mono"
                autoComplete="off"
              />
            </div>
            <button
              type="button"
              onClick={saveApiKeyAndClose}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-xs font-bold shrink-0 disabled:opacity-50"
              disabled={settingsBusy}
            >
              {settingsBusy ? "Saving..." : "Save key"}
            </button>
            <button
              type="button"
              onClick={() => refreshModelCatalog({ force: true })}
              disabled={modelsBusy}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-xl text-xs font-bold shrink-0 disabled:opacity-50"
            >
              {modelsBusy ? "Refreshing..." : "Refresh models"}
            </button>
          </div>
          <p className="text-[11px] text-slate-400">
            {settingsMeta.updatedBy
              ? `Last changed by ${settingsMeta.updatedBy}${formatChangedAt(settingsMeta.updatedAt) ? ` · ${formatChangedAt(settingsMeta.updatedAt)}` : ""}`
              : "No one has saved a model and key yet."}
          </p>
        </div>
      )}

      {activeTab === "chat" && (
        <div className="flex flex-1 min-h-0 flex-col">
          {!apiKey.trim() && (
            <div className="mx-5 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-center gap-2">
              <FiAlertTriangle className="shrink-0" />
              Open the settings icon beside Preview and save the shared Anthropic key. It must start with sk-ant-.
            </div>
          )}
          <div className="flex flex-1 min-h-[560px] overflow-hidden">
            <div
              className={`flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 ${
                showCanvas && htmlOut ? "w-1/2" : "w-full"
              }`}
            >
              <div className="flex flex-wrap gap-2 px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-xs bg-slate-50 dark:bg-slate-950 items-center">
                <select
                  value={selectedTemplateId ? `${TEMPLATE_VALUE_PREFIX}${selectedTemplateId}` : docType}
                  onChange={(e) => onTemplatePickerChange(e.target.value)}
                  disabled={templatesBusy}
                  className="border rounded-lg px-2 py-1 bg-white dark:bg-slate-900 max-w-xs"
                  title="Document type or saved company template"
                >
                  {DOC_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                  {companyTemplates.map((tpl) => (
                    <option key={tpl.id} value={`${TEMPLATE_VALUE_PREFIX}${tpl.id}`}>
                      {tpl.name}
                    </option>
                  ))}
                </select>
                {selectedTemplateId && (
                  <button
                    type="button"
                    onClick={deleteSelectedTemplate}
                    disabled={templatesBusy}
                    className="inline-flex items-center gap-1 text-slate-500 hover:text-red-600 disabled:opacity-40"
                    title="Delete selected company template"
                  >
                    <FiTrash2 />
                  </button>
                )}
                <select
                  value={officeLocation}
                  onChange={(e) => setOfficeLocation(e.target.value)}
                  className="border rounded-lg px-2 py-1 bg-white dark:bg-slate-900"
                >
                  <option value="corporate">Corporate office</option>
                  <option value="houston">Houston office</option>
                </select>

                <label className="inline-flex items-center gap-1 cursor-pointer text-slate-600" title="Match this template style (image or HTML)">
                  <FiUpload />
                  <input
                    type="file"
                    accept=".html,.htm,.md,.txt,image/*,.png,.jpg,.jpeg,.webp,text/html,text/markdown,text/plain"
                    className="hidden"
                    onChange={(e) => {
                      onUploadReference(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                  Match file
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer text-slate-600" title="Upload HTML and use as match reference">
                  <FiPlus />
                  <input
                    type="file"
                    accept=".html,.htm,text/html"
                    className="hidden"
                    onChange={(e) => {
                      onUploadAsSavedTemplate(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                  New template
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer text-slate-500">
                  <FiPaperclip />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const r = new FileReader();
                      r.onloadend = () => {
                        setClientLogoBase64(r.result);
                        toastMsg("Client logo attached");
                      };
                      r.readAsDataURL(f);
                    }}
                  />
                  Logo
                </label>
                <button
                  type="button"
                  onClick={() => setFieldPickerOpen(true)}
                  className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 font-semibold ${
                    selectedFieldKeys.length
                      ? "border-red-300 bg-red-50 text-red-700"
                      : "border-slate-300 bg-white text-slate-700"
                  }`}
                  title="Choose the API fields (project, contacts, financial...) the template must include"
                >
                  <FiDatabase />
                  API fields{selectedFieldKeys.length ? ` (${selectedFieldKeys.length})` : ""}
                </button>

                <select
                  className="border rounded-lg px-2 py-1 bg-white dark:bg-slate-900 max-w-[140px] ml-auto"
                  value={chatId}
                  onChange={(e) => {
                    if (e.target.value === "__new__") startNewChat();
                    else loadChat(e.target.value);
                  }}
                  title="Chat history"
                >
                  <option value={chatId}>This chat</option>
                  <option value="__new__">+ New chat</option>
                  {chatThreads
                    .filter((t) => t.id !== chatId)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                </select>
                <button type="button" onClick={saveChatNow} className="inline-flex items-center gap-1 px-2 py-1 border rounded-lg font-semibold bg-white dark:bg-slate-900" title="Save chat history">
                  <FiSave /> Save chat
                </button>
              </div>

              {referenceFile && (
                <div className="px-4 py-1.5 text-[11px] bg-indigo-50 text-indigo-800 border-b border-indigo-100 flex justify-between gap-2 items-center">
                  <span>
                    Matching reference: <strong>{referenceFile.name}</strong>
                    {referenceFile.isImage ? " (image → clone layout, not house BUDGET)" : " (layout match mode)"}
                  </span>
                  <div className="flex items-center gap-2">
                    {referenceFile.isImage && referenceFile.dataUrl && (
                      <img src={referenceFile.dataUrl} alt="" className="h-8 w-auto rounded border border-indigo-200" />
                    )}
                    <button type="button" className="underline" onClick={() => setReferenceFile(null)}>
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {selectedFieldKeys.length > 0 && (
                <div className="px-4 py-1.5 text-[11px] bg-red-50 text-red-800 border-b border-red-100 flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <strong>{selectedFieldKeys.length}</strong> API field{selectedFieldKeys.length === 1 ? "" : "s"} will be
                    sent to the agent and placed in the template
                  </span>
                  <span className="flex items-center gap-3">
                    <button type="button" className="underline" onClick={() => setFieldPickerOpen(true)}>
                      Edit fields
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      className="font-semibold underline disabled:opacity-40"
                      onClick={() => send("Place all of the selected API fields in the template, grouped by section.")}
                    >
                      Place with agent
                    </button>
                  </span>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[90%] rounded-2xl px-4 py-3 text-xs leading-relaxed whitespace-pre-wrap ${
                        msg.role === "user"
                          ? "bg-red-600 text-white rounded-tr-sm"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-tl-sm"
                      }`}
                    >
                      <ChatBody text={msg.text} />
                      {msg.suggestions?.length > 0 && (
                        <div className="mt-3 flex flex-col gap-1.5">
                          {msg.suggestions.map((choice) => (
                            <button
                              key={choice}
                              type="button"
                              disabled={busy}
                              onClick={() => send(choice)}
                              className="text-left px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-[11px] font-semibold hover:border-red-400 hover:text-red-700 disabled:opacity-40"
                            >
                              {choice}
                            </button>
                          ))}
                        </div>
                      )}
                      {msg.card && htmlOut && (
                        <div className="mt-3 p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2 text-slate-800 dark:text-slate-100">
                          <div className="font-bold flex items-center gap-2">
                            <FiFileText className="text-red-500" /> {docType} ready
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => {
                                setShowCanvas(true);
                                setCanvasTab("preview");
                              }}
                              className="flex-1 min-w-[100px] py-1.5 bg-red-600 text-white rounded-lg font-bold"
                            >
                              View preview
                            </button>
                            <button type="button" onClick={downloadHtml} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg font-semibold" title="Download HTML">
                              <FiDownload />
                            </button>
                            <button type="button" onClick={saveAsPdf} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg font-semibold inline-flex items-center gap-1" title="Save as PDF">
                              <FiPrinter /> PDF
                            </button>
                            <button type="button" onClick={saveCurrentAsTemplate} className="px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-lg font-semibold inline-flex items-center gap-1">
                              <FiSave /> Save template
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="text-[10px] opacity-60 mt-1">{msg.time}</div>
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <FiRefreshCw className="animate-spin" /> {status || "Working..."}
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 flex gap-2 overflow-x-auto items-center">
                <button type="button" onClick={loadSample} className="shrink-0 px-3 py-1 rounded-full border text-[11px] font-semibold bg-white dark:bg-slate-800">
                  <FiZap className="inline text-amber-500" /> Sample OU Life Sciences
                </button>
                <div className="flex items-center gap-1 ml-auto">
                  <input
                    value={saveTplName}
                    onChange={(e) => setSaveTplName(e.target.value)}
                    placeholder="New template name"
                    className="border rounded-lg px-2 py-1 text-[11px] w-36 bg-white dark:bg-slate-900"
                  />
                  <button
                    type="button"
                    onClick={saveCurrentAsTemplate}
                    disabled={!htmlOut || templatesBusy}
                    className="shrink-0 px-3 py-1 rounded-full border text-[11px] font-semibold bg-emerald-50 text-emerald-800 disabled:opacity-40"
                  >
                    <FiSave className="inline" /> Save as template
                  </button>
                  {selectedTemplateId && (
                    <button
                      type="button"
                      onClick={updateCurrentTemplate}
                      disabled={!htmlOut || templatesBusy}
                      className="shrink-0 px-3 py-1 rounded-full border text-[11px] font-semibold bg-white dark:bg-slate-800 disabled:opacity-40"
                    >
                      <FiSave className="inline" /> Update template
                    </button>
                  )}
                </div>
              </div>

              <form
                className="p-4 border-t border-slate-200 dark:border-slate-800"
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
              >
                <div className="flex gap-2 bg-slate-100 dark:bg-slate-800 rounded-2xl p-2 pl-3 border border-slate-200 dark:border-slate-700 focus-within:border-red-500">
                  <textarea
                    rows={2}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder="Ask a question or describe the template. Paste a takeoff, or say “build it now”, when you want the document."
                    className="flex-1 bg-transparent text-xs resize-none focus:outline-none leading-relaxed"
                  />
                  <button
                    type="submit"
                    disabled={busy || !input.trim()}
                    className="w-10 h-10 rounded-xl bg-slate-900 hover:bg-red-600 text-white flex items-center justify-center disabled:opacity-40 shrink-0"
                  >
                    <FiSend className="text-sm" />
                  </button>
                </div>
              </form>
            </div>

            {showCanvas && (
              <div className={`${htmlOut ? "w-1/2" : "hidden"} flex flex-col bg-slate-100 dark:bg-slate-950 p-3`}>
                <div className="flex items-center justify-between pb-2 gap-2">
                  <div className="min-w-0">
                    <h2 className="font-bold text-sm">Template preview</h2>
                    <p className="text-[11px] text-slate-500">Click a highlighted field and type</p>
                  </div>
                  {htmlOut && (
                    <div className="flex gap-2">
                      <button type="button" onClick={openHtml} className="px-3 py-1.5 text-xs font-semibold bg-white dark:bg-slate-800 border rounded-xl">
                        Open
                      </button>
                      <button type="button" onClick={downloadHtml} className="px-3 py-1.5 text-xs font-bold bg-white dark:bg-slate-800 border rounded-xl flex items-center gap-1">
                        <FiDownload /> Download
                      </button>
                      <button type="button" onClick={saveAsPdf} className="px-3 py-1.5 text-xs font-bold bg-red-600 text-white rounded-xl flex items-center gap-1">
                        <FiPrinter /> Save as PDF
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mb-2 text-xs">
                  {["preview", "notes", "code"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setCanvasTab(t)}
                      className={`px-3 py-1 rounded-lg font-bold capitalize ${
                        canvasTab === t ? "bg-white dark:bg-slate-800 text-red-600 shadow-sm" : "text-slate-400"
                      }`}
                    >
                      {t === "notes" ? `Notes (${notes.length})` : t}
                    </button>
                  ))}
                </div>
                <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                  {htmlOut ? (
                    canvasTab === "preview" ? (
                      <iframe title="preview" srcDoc={htmlOut} className="w-full h-full border-0 bg-white min-h-[480px]" />
                    ) : canvasTab === "notes" ? (
                      <ul className="p-4 space-y-2 overflow-y-auto h-full text-xs">
                        {notes.map((n, i) => (
                          <li key={i} className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-100 dark:border-amber-900/40">
                            {n}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <textarea readOnly value={htmlOut} className="w-full h-full min-h-[480px] p-3 font-mono text-[11px] bg-slate-950 text-slate-300 border-0" />
                    )
                  ) : (
                    <div className="h-full min-h-[480px] flex flex-col items-center justify-center text-slate-400 text-xs">
                      <FiTerminal className="text-3xl mb-2" />
                      Send a prompt to build a template
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "skill" && (
        <div className="p-5 flex flex-col gap-3 flex-1 min-h-0">
          <h2 className="font-bold text-sm flex items-center gap-2">
            <FiZap className="text-amber-500" /> nettles-template-builder
          </h2>
          <textarea readOnly value={skillMd || "Loading skill..."} className="flex-1 min-h-[480px] bg-slate-950 text-slate-300 font-mono text-[11px] p-4 rounded-2xl" />
        </div>
      )}

      {activeTab === "rules" && (
        <div className="p-5 flex flex-col gap-3 flex-1 min-h-0">
          <h2 className="font-bold text-sm flex items-center gap-2">
            <FiBookOpen className="text-red-500" /> house-rules.md ({parsedRules.length} rules)
          </h2>
          <textarea
            value={rulesMd}
            onChange={(e) => {
              setRulesMd(e.target.value);
              parseRules(e.target.value);
            }}
            className="flex-1 min-h-[480px] bg-slate-950 text-slate-300 font-mono text-[11px] p-4 rounded-2xl"
          />
          <p className="text-[11px] text-slate-500">
            The rules shown here are included with each Anthropic request.
          </p>
        </div>
      )}
    </div>
  );
}
